import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { groupBy } from 'lodash';
import { Day, F2FConsultantType, RandoxSlotType, StyledSlotBox, StyledWeekButton } from './SlotGrid';
import { ArrowLeftOutlined, ArrowRightOutlined } from 'antd/icons';
import type { Clinician as ClinicianType } from 'vl-common/src/schemas/getCliniciansClinicians';
import { AppointmentSlot, RandoxSlot } from 'vl-common/src/types';
import { useViewportSize } from 'vl-common/src/hooks/useWindowSize';

interface F2FSlotsProps {
  clinician: ClinicianType | F2FConsultantType | RandoxSlotType;
  slots: AppointmentSlot[] | RandoxSlot[];
  timezone?: string;
  weekCount: number;
  maxWeekCount: number;
  setWeekCount: Dispatch<SetStateAction<number>>;
  selectSlot: (slot: AppointmentSlot | RandoxSlot) => void;
  selectedId?: string;
  mobileView?: boolean;
  slotTooltipUtc?: boolean;
}

const SLOTS_SLICE_OPTIONS = {
  WEEK: [0, 7],
  START_OF_THE_WEEK: [0, 4],
  END_OF_THE_WEEK: [3, 7]
};

const WEEK_PARTS = {
  START_OF_THE_WEEK: 'START_OF_THE_WEEK',
  END_OF_THE_WEEK: 'END_OF_THE_WEEK'
} as const;

type WeekPart = (typeof WEEK_PARTS)[keyof typeof WEEK_PARTS];

export const F2FSlotGrid: React.FC<F2FSlotsProps> = ({
  clinician,
  slots = [],
  timezone,
  weekCount,
  maxWeekCount,
  setWeekCount,
  selectSlot,
  selectedId,
  mobileView,
  slotTooltipUtc
}) => {
  const { t } = useTranslation('common');
  const { width } = useViewportSize();
  const isMobile = mobileView || width < 992;

  const clinicianTestId = (
    ((clinician as ClinicianType).full_name ?? '') ||
    ((clinician as RandoxSlotType).Name ?? '') ||
    `${(clinician as F2FConsultantType).first_name ?? ''} ${(clinician as F2FConsultantType).last_name ?? ''}`
  )
    .trim()
    .replaceAll(/\s+/g, '-')
    .toLowerCase();

  const initialWeekChunk = useMemo<WeekPart>(() => {
    if (!slots?.length) return WEEK_PARTS.START_OF_THE_WEEK;
    const firstWeekday = DateTime.fromJSDate(slots[0]?.start).setZone(timezone).weekday;
    return firstWeekday <= 3 ? WEEK_PARTS.START_OF_THE_WEEK : WEEK_PARTS.END_OF_THE_WEEK;
  }, [slots, timezone]);
  const [currentWeekPart, setCurrentWeekPart] = useState<WeekPart>(initialWeekChunk);

  const getSliceParams = () =>
    currentWeekPart === WEEK_PARTS.START_OF_THE_WEEK
      ? SLOTS_SLICE_OPTIONS.START_OF_THE_WEEK
      : SLOTS_SLICE_OPTIONS.END_OF_THE_WEEK;

  const slotsByDay: { [isoDate: string]: AppointmentSlot[] } = useMemo(() => {
    const firstAvailableSlot = slots?.length
      ? DateTime.fromJSDate(slots?.[0]?.start).setZone(timezone)
      : DateTime.local().setZone(timezone);
    const weekStart = firstAvailableSlot.startOf('week');

    const emptyWeek = Array.from({ length: 7 }).reduce<{ [isoDate: string]: AppointmentSlot[] }>((acc, _, i) => {
      const day = weekStart.plus({ days: i }).toISODate();
      if (day) acc[day] = [];
      return acc;
    }, {});

    return {
      ...emptyWeek,
      ...groupBy(slots, (slot: AppointmentSlot) => DateTime.fromJSDate(slot.start).setZone(timezone).toISODate())
    };
  }, [slots, timezone]);

  const getSlots = (): [string, AppointmentSlot[]][] => {
    const entries = Object.entries(slotsByDay);
    return isMobile ? entries.slice(...getSliceParams()) : entries;
  };
  const goForward = () => {
    if (!isMobile) {
      setWeekCount((prev) => prev + 1);
      return;
    }

    if (currentWeekPart === WEEK_PARTS.END_OF_THE_WEEK) {
      setCurrentWeekPart(WEEK_PARTS.START_OF_THE_WEEK);
      setWeekCount((prev) => prev + 1);
      return;
    }
    setCurrentWeekPart(WEEK_PARTS.END_OF_THE_WEEK);
  };

  const goBackward = () => {
    if (!isMobile) {
      setWeekCount((prev) => prev - 1);
      return;
    }

    if (currentWeekPart === WEEK_PARTS.START_OF_THE_WEEK) {
      setCurrentWeekPart(WEEK_PARTS.END_OF_THE_WEEK);
      setWeekCount((prev) => prev - 1);
      return;
    }

    setCurrentWeekPart(WEEK_PARTS.START_OF_THE_WEEK);
  };

  const hasSlotsInPart = (part: WeekPart) => {
    const [start, end] = SLOTS_SLICE_OPTIONS[part];

    return Object.entries(slotsByDay)
      .slice(start, end)
      .some(([, daySlots]) => daySlots.length > 0);
  };

  const disableNavigationBack = (() => {
    if (!slots?.length) return true;

    if (!isMobile) {
      return weekCount === 0;
    }

    // Disable back navigation for mobile if on first week’s start or if on first week’s end with no slots for week's start
    return (
      (weekCount === 0 && currentWeekPart === WEEK_PARTS.START_OF_THE_WEEK) ||
      (weekCount === 0 &&
        currentWeekPart === WEEK_PARTS.END_OF_THE_WEEK &&
        !hasSlotsInPart(WEEK_PARTS.START_OF_THE_WEEK))
    );
  })();

  const disableNavigationForward = (() => {
    if (!(maxWeekCount || maxWeekCount === 0)) return false;

    if (!isMobile) {
      return weekCount >= maxWeekCount;
    }

    // Disable forward navigation for mobile if at the last week’s end or if no slots for week’s end part
    return (
      (weekCount >= maxWeekCount && currentWeekPart === WEEK_PARTS.END_OF_THE_WEEK) ||
      (currentWeekPart === WEEK_PARTS.START_OF_THE_WEEK && !hasSlotsInPart(WEEK_PARTS.END_OF_THE_WEEK))
    );
  })();

  return (
    <StyledSlotBox>
      <div className="columns">
        <StyledWeekButton
          data-testid={`${clinicianTestId}-go-backward`}
          onClick={goBackward}
          disabled={disableNavigationBack}
          aria-label={t('Previous week')}
          icon={<ArrowLeftOutlined aria-hidden />}
        />
        {getSlots().map(([day, slots]) => (
          <Day
            key={day}
            clinician={clinician}
            day={day}
            slots={slots}
            timezone={timezone}
            selectSlot={selectSlot}
            selectedId={selectedId}
            slotCount={5}
            slotTooltipUtc={slotTooltipUtc}
          />
        ))}
        <StyledWeekButton
          data-testid={`${clinicianTestId}-go-forward`}
          onClick={goForward}
          disabled={!!disableNavigationForward}
          aria-label={t('Next week')}
          icon={<ArrowRightOutlined aria-hidden />}
        />
      </div>
    </StyledSlotBox>
  );
};
